import { Group } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setActivePanel,
  setSelectedDataPoint,
} from '../../../../../store/process-analysis-store/process-analysis-store.actions';
import { secondaryBase, skyDark } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../../ui-library/typography/typography';
import { formatMeasureName } from '../../../process-analysis.helpers';
import { useMeasureMetadata } from './side-section.hooks';

export const SideSectionHeader = () => {
  const { t } = useTranslation();
  const measureMetadata = useMeasureMetadata();
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  const measureName = measureMetadata ? formatMeasureName(measureMetadata.measure_name, t) : '';

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        // Use setTimeout to ensure the element is fully rendered
        setTimeout(() => {
          if (textRef.current) {
            const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
            setIsTextTruncated(isOverflowing);
          }
        }, 0);
      }
    };

    if (measureName) {
      checkTruncation();
      window.addEventListener('resize', checkTruncation);
    }

    return () => window.removeEventListener('resize', checkTruncation);
  }, [measureName]);

  if (!measureMetadata) {
    return null;
  }

  const handleClose = () => {
    setActivePanel(null);
    setSelectedDataPoint(null);
  };

  return (
    <Group justify="space-between" align="center" wrap="nowrap" h={30}>
      <Group gap={8} align="center" style={{ flex: 1, minWidth: 0 }} wrap="nowrap">
        <Tooltip
          label={measureMetadata.measure_description}
          variant="white"
          multiline
          w={300}
          style={{ lineHeight: 1.5 }}
          zIndex={1000}
        >
          <Icon name="info" size={20} color={secondaryBase} style={{ flexShrink: 0 }} pointer />
        </Tooltip>
        <Tooltip label={measureName} disabled={!isTextTruncated} variant="white">
          <Text
            ref={textRef}
            weight="bold"
            size="medium"
            style={{
              lineHeight: 1.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
              cursor: 'default',
            }}
          >
            {measureName}
          </Text>
        </Tooltip>
      </Group>
      <Icon name="close" size={20} color={skyDark} onClick={handleClose} aria-label="Close panel" />
    </Group>
  );
};
